import { Type } from 'lucide-react'
import { Pie, PieChart } from 'recharts'

import EmptyCard from './EmptyCard'

import type { ImpactResponse } from '@/api/teamInsights'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { formatTooltipNumber } from '@/lib/utils'

const AUTO_COLOR = 'var(--chart-1)'
const MANUAL_COLOR = 'var(--chart-2)'

const chartConfig = {
  count: { label: 'Items' },
  auto_learned: { label: 'Auto-learned items', color: AUTO_COLOR },
  manually_added: { label: 'Manually added items', color: MANUAL_COLOR },
} satisfies ChartConfig

interface DictionaryChartProps {
  response: ImpactResponse['dictionary']
}

const DictionaryChart = ({ response }: DictionaryChartProps) => {
  if (response.total === 0) {
    return (
      <EmptyCard
        icon={<Type className="size-5" />}
        title="No dictionary items added"
        description="Add words and phrases that Flow should always recognize or format correctly"
        cta={{
          label: 'Open dictionary',
          onClick: () => {
            window.location.href = 'wispr-flow://open/dictionary'
          },
        }}
      />
    )
  }

  const chartData = [
    {
      key: 'auto_learned',
      label: 'Auto-learned items',
      count: response.auto_learned,
      fill: AUTO_COLOR,
    },
    {
      key: 'manually_added',
      label: 'Manually added items',
      count: response.manually_added,
      fill: MANUAL_COLOR,
    },
  ]

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-0.5">
          <CardTitle className="text-xl font-semibold">
            {response.total.toLocaleString()} dictionary items added
          </CardTitle>
          <p className="text-[15px] leading-5 text-muted-foreground">
            Unique words your team has added or that Flow has learned to recognize
          </p>
        </div>
      </CardHeader>
      <CardContent className="h-[280px] px-6 py-4">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  className="w-fit max-w-none whitespace-nowrap"
                  hideLabel
                  formatter={(value, _name, item) => (
                    <div className="flex w-full items-center justify-between gap-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: item.payload?.fill }} />
                        <span className="text-muted-foreground">
                          {(item.payload as { label?: string } | undefined)?.label}
                        </span>
                      </div>
                      <span className="font-semibold text-foreground">{formatTooltipNumber(Number(value))}</span>
                    </div>
                  )}
                />
              }
            />
            <Pie data={chartData} dataKey="count" nameKey="key" innerRadius={0} strokeWidth={2} />
            <ChartLegend verticalAlign="bottom" content={<ChartLegendContent nameKey="key" />} />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export default DictionaryChart
